import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'

const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

// The job titles and locations a user wants searched. Every title is searched in every
// location, so two titles and two locations make four searches.
export const useJobSearchStore = defineStore('jobSearch', {
  state: () => ({
    titles: [],
    locations: [],
    limits: { titles: 10, locations: 10 },
    // How the extension behaves; it reads these live, so a change applies to the next search.
    settings: {
      max_pages: 5, show_fetched_icon: true, fetch_company_from_linkedin: false, fetch_company_from_website: false,
      ai_enabled: false, ai_model: 'gpt-5-mini', ai_resume_prompt: '', ai_resume_prompt_custom: false,
      limits: { max_pages: { min: 1, max: 10 } },
    },
    // Per search, keyed like "<title id>:<location id>": { jobs_count, last_fetched_at }.
    stats: {},
    // The searches the user turned off, keyed the same way. They stay out of the plan until restored.
    excluded: {},
    fetchedAt: null,
    loading: false,
    error: null,
  }),

  getters: {
    isCacheValid: (state) => state.fetchedAt !== null && Date.now() - state.fetchedAt < CACHE_TTL,

    // Every title in every location, turned off or not. A search that was just added has no history
    // yet, so it shows no jobs and no date.
    allCombinations: (state) =>
      state.titles.flatMap((title) =>
        state.locations.map((location) => {
          const key = `${title.id}:${location.id}`
          return {
            key,
            job_title_id: title.id,
            job_location_id: location.id,
            title: title.name,
            location: location.name,
            jobs_count: state.stats[key]?.jobs_count || 0,
            last_fetched_at: state.stats[key]?.last_fetched_at || null,
            excluded: Boolean(state.excluded[key]),
          }
        }),
      ),

    // The searches that will run.
    combinations() {
      return this.allCombinations.filter((combination) => !combination.excluded)
    },

    // The searches the user turned off, which can be brought back.
    excludedCombinations() {
      return this.allCombinations.filter((combination) => combination.excluded)
    },

    canAddTitle: (state) => state.titles.length < state.limits.titles,
    canAddLocation: (state) => state.locations.length < state.limits.locations,
    isReady: (state) => state.titles.length > 0 && state.locations.length > 0,
  },

  actions: {
    async fetchIfStale() {
      if (this.isCacheValid) return
      await this.fetchSettings()
    },

    // `silent` refreshes in the background: what is on screen stays, with no loading state.
    async fetchSettings({ silent = false } = {}) {
      if (!silent) this.loading = true
      this.error = null
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', '/api/v1/job_search_settings', {}, false)
        if (!res.js_error && res.response.success) {
          this.titles = res.response.data.titles
          this.locations = res.response.data.locations
          this.limits = res.response.data.limits
          this.settings = res.response.data.settings
          this.stats = Object.fromEntries(
            res.response.data.combinations.map((c) => [c.key, { jobs_count: c.jobs_count, last_fetched_at: c.last_fetched_at }]),
          )
          this.excluded = Object.fromEntries(res.response.data.combinations.filter((c) => c.excluded).map((c) => [c.key, true]))
          this.fetchedAt = Date.now()
        } else {
          this.error = res.response?.message || 'Failed to load your searches.'
        }
      } catch {
        this.error = 'Something went wrong.'
      } finally {
        this.loading = false
      }
    },

    // Each add and remove returns { ok, message } so the list can show a server message,
    // for example "You can add up to 10 job titles.", next to the field.
    addTitle(name) {
      return this.addTerm('/api/v1/job_titles', 'titles', name)
    },

    addLocation(name) {
      return this.addTerm('/api/v1/job_locations', 'locations', name)
    },

    removeTitle(id) {
      return this.removeTerm('/api/v1/job_titles', 'titles', id)
    },

    removeLocation(id) {
      return this.removeTerm('/api/v1/job_locations', 'locations', id)
    },

    async addTerm(path, list, name) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('POST', path, { name }, false)
      if (!res.js_error && res.response?.success) {
        this[list].push(res.response.data)
        this.fetchedAt = Date.now()
        return { ok: true }
      }
      return { ok: false, message: res.response?.message || 'Could not add that. Please try again.' }
    },

    async removeTerm(path, list, id) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('DELETE', `${path}/${id}`, {}, false)
      if (!res.js_error && res.response?.success) {
        this[list] = this[list].filter((term) => term.id !== id)
        this.fetchedAt = Date.now()
        return { ok: true }
      }
      return { ok: false, message: res.response?.message || 'Could not remove that. Please try again.' }
    },

    // Turns one search off (remove from the plan) or back on. The screen changes at once, so the row can
    // leave with its animation, and it is put back if the server refuses. Returns { ok, message }.
    async setSearchExcluded(combination, excluded) {
      const { key, job_title_id: jobTitleId, job_location_id: jobLocationId } = combination
      this.excluded = { ...this.excluded, [key]: excluded }

      const { axiosRequest } = useRequest()
      const res = await axiosRequest('POST', `/api/v1/job_searches/${excluded ? 'exclude' : 'restore'}`,
        { job_title_id: jobTitleId, job_location_id: jobLocationId }, false)
      if (!res.js_error && res.response?.success) {
        this.fetchedAt = Date.now()
        return { ok: true }
      }

      this.excluded = { ...this.excluded, [key]: !excluded }
      const fallback = excluded ? 'Could not remove that search. Please try again.' : 'Could not restore that search. Please try again.'
      return { ok: false, message: res.response?.message || res.error?.data?.message || fallback }
    },

    // Applies the change straight away and puts it back if the server refuses, so the
    // control never waits on the network. Returns { ok, message }.
    async updateSettings(patch) {
      const previous = this.settings
      this.settings = { ...this.settings, ...patch }

      const { axiosRequest } = useRequest()
      const res = await axiosRequest('PUT', '/api/v1/job_tracker_settings', patch, false)
      if (!res.js_error && res.response?.success) {
        this.settings = res.response.data
        this.fetchedAt = Date.now()
        return { ok: true }
      }

      this.settings = previous
      return { ok: false, message: res.response?.message || 'Could not save that. Please try again.' }
    },

    invalidate() {
      this.fetchedAt = null
    },
  },
})
